import { FC, PropsWithChildren } from 'react';

import numeral from 'numeral';
import { AvailableCurrencies, CURRENCY_SYMBOLS } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';

import { HSpacing } from '@hh.ru/magritte-ui';

import Conversion from 'bloko/blocks/conversion';
import Text from 'bloko/blocks/text';

import useTrls from 'hooks/translation/useTrls';

import Table from 'components/Table';

import { ActType } from 'components/Admin/Reports/ActsReport/Labels';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';

import { ActType as ActTypeEnum } from 'store/models/acts';
import { Summary } from 'store/models/actsSummary';
import { useSelector } from 'store/utils';

import 'components/Admin/Reports/ActsSummaryReport/ActsSummaryInfo/ActsSummaryInfo.less';

const ActsSummaryInfo: FC<
    {
        data: Summary;
    } & PropsWithChildren
> = ({ data }) => {
    const appliedCurrency = useSelector((state) => state.actsSummary.appliedCurrency);

    const trl = useTrls();

    const HH_LABEL = <ActType type={ActTypeEnum.HH} />;
    const CLICKME_LABEL = <ActType type={ActTypeEnum.Clickme} />;

    if (!data) {
        return null;
    }

    const { clickme, hh } = data;
    const sumFormat = appliedCurrency === AvailableCurrencies.RUR ? '0,0' : '0,0.00';

    return (
        <div className="acts-summary-info">
            <Table className="acts-summary-info-content">
                <TableBody>
                    <TableRow>
                        <TableCell className="acts-summary-info-cell-left">
                            <Conversion
                                zero={trl('admin.actsReport.actsTable.total.acts.zero')}
                                one={trl('admin.actsReport.actsTable.total.acts.one')}
                                some={trl('admin.actsReport.actsTable.total.acts.some')}
                                many={trl('admin.actsReport.actsTable.total.acts.many')}
                                value={clickme.count + hh.count}
                                hasValue={false}
                            />
                        </TableCell>
                        <TableCell className="acts-summary-info-cell-right">
                            <Text Element="span" strong>
                                {clickme.count + hh.count}
                            </Text>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="acts-summary-info-cell-left">{CLICKME_LABEL}</TableCell>
                        <TableCell className="acts-summary-info-cell-right">{clickme.count}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="acts-summary-info-cell-left">{HH_LABEL}</TableCell>
                        <TableCell className="acts-summary-info-cell-right">{hh.count}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <div className="acts-summary-info-divider" />
            <Table className="acts-summary-info-content">
                <TableBody>
                    <TableRow>
                        <TableCell className="acts-summary-info-cell-left">
                            {trl('admin.actsReport.actsTable.total.amount')}
                            <HSpacing default={8} />
                        </TableCell>
                        <TableCell className="acts-summary-info-cell-right">
                            <Text Element="span" strong>
                                {numeral(clickme.sum + hh.sum).format(sumFormat)}
                            </Text>
                            <Text Element="span">
                                {NON_BREAKING_SPACE}
                                {CURRENCY_SYMBOLS[appliedCurrency]}
                            </Text>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="acts-summary-info-cell-left">
                            {CLICKME_LABEL}
                            <HSpacing default={8} />
                        </TableCell>
                        <TableCell className="acts-summary-info-cell-right">
                            <Text Element="span" strong>
                                {numeral(clickme.sum).format(sumFormat)}
                            </Text>
                            <Text Element="span">
                                {NON_BREAKING_SPACE}
                                {CURRENCY_SYMBOLS[appliedCurrency]}
                            </Text>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="acts-summary-info-cell-left">
                            {HH_LABEL}
                            <HSpacing default={8} />
                        </TableCell>
                        <TableCell className="acts-summary-info-cell-right">
                            <Text Element="span" strong>
                                {numeral(hh.sum).format(sumFormat)}
                            </Text>
                            <Text Element="span">
                                {NON_BREAKING_SPACE}
                                {CURRENCY_SYMBOLS[appliedCurrency]}
                            </Text>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
};

export default ActsSummaryInfo;
